import React, { memo, useEffect, useState } from "react";
import styled from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import ShowMoreButton from "@components/atoms/Filters/ShowMoreButton";
import { VDBText } from "@components/atoms";
import { useTheme } from "styled-components/native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTranslation } from "react-i18next";
import { CPSSubsectionNames } from "../TextListSection/types";
import { ProductCategory } from "@utils/constants";
import { css } from "styled-components/native";
import { useSearchFiltersSlice } from "@redux/slices/searchFilters";
import { DefaultTheme } from "styled-components/native";

const ListSection = ({
  data = [],
  children = [],
  direction = "column",
  step = 0,
  selectedProduct = ProductCategory.DIAMOND,
  isCollapsible = false,
  sectionName,
}: any) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const typography = useTypographyStore();
  const { auxPayload: filtersPayload } = useSearchFiltersSlice();
  const maxChildren =
    (theme.isLargeScreen || theme.isTablet) && data?.max_row_web
      ? data.max_row_web
      : !(theme.isLargeScreen || theme.isTablet) && data?.max_row
      ? data.max_row
      : children.length;
  const [showAll, setShowAll] = useState<boolean>(!data?.max_row);
  const visibleChildren = children.slice(0, maxChildren);
  const hiddenChildren = children.slice(maxChildren, children.length);

  const expandView = () => {};

  useEffect(() => {
    if (
      data?.name === CPSSubsectionNames.CPS &&
      (filtersPayload?.[sectionName]?.hasOwnProperty("cut_from") ||
        filtersPayload?.[sectionName]?.hasOwnProperty("cut_to") ||
        filtersPayload?.[sectionName]?.hasOwnProperty("polish_from") ||
        filtersPayload?.[sectionName]?.hasOwnProperty("polish_to") ||
        filtersPayload?.[sectionName]?.hasOwnProperty("symmetry_from") ||
        filtersPayload?.[sectionName]?.hasOwnProperty("symmetry_to"))
    ) {
      setShowAll(true);
    }
  }, [filtersPayload]);

  return (
    <ListView>
      {data?.display_title && !isCollapsible && (
        <TextContainer
          showGap={
            data.name === "Discount"
              ? theme.isLargeScreen || theme.isTablet
                ? step === 1
                : false
              : children.length === 1
          }
        >
          <VDBText type={typography["H3-M"]} color={theme["core-05"]}>
            {t(data?.label)}
          </VDBText>
        </TextContainer>
      )}
      <ChildrenContainer direction={direction} showGap={children.length === 1}>
        {data?.name === CPSSubsectionNames.CPS
          ? React.Children.map(visibleChildren, (child) =>
              React.isValidElement(child)
                ? React.cloneElement(child, { expandView } as any)
                : child
            )
          : visibleChildren}
        <HiddenChildrenContainer
          direction={direction}
          showAll={showAll}
          name={data?.name}
          step={step}
        >
          {hiddenChildren}
        </HiddenChildrenContainer>
      </ChildrenContainer>
      {!!(theme.isLargeScreen || theme.isTablet
        ? data?.max_row_web || 0
        : data?.max_row || 0) &&
        (data?.sub_filters || []).length > (data?.max_row || 0) * 1 && (
          <ButtonContainer>
            <ShowMoreButton
              options={data?.sub_filters || []}
              columns={1}
              showAll={showAll}
              onPressFunction={setShowAll}
              displayedRows={data?.max_row || 0}
              selectedProduct={selectedProduct}
            />
          </ButtonContainer>
        )}
    </ListView>
  );
};

const ListView = styled.View`
  flex-direction: column;
  gap: ${SpaceType.Extra}px;
`;

const ChildrenContainer = styled.View<{
  direction?: string;
  showGap?: boolean;
}>`
  ${({ direction }: { direction?: string }) =>
    direction === "row"
      ? css`
          flex-direction: row;
          flex-wrap: wrap;
        `
      : css`
          flex-direction: column;
        `}
  ${({ showGap }: { showGap?: boolean }) =>
    !showGap &&
    css`
      gap: ${SpaceType.Basic}px;
    `};
`;

const HiddenChildrenContainer = styled.View<{
  direction?: string;
  showAll?: boolean;
  name?: string;
  step?: number;
}>`
  ${({
    name,
    step,
    theme,
  }: {
    name?: string;
    step?: number;
    theme?: DefaultTheme;
  }) =>
    name === "Measuerements" &&
    step === 1 &&
    theme?.isLargeScreen &&
    css`
      flex: 1;
    `}
  ${({
    name,
    step,
    theme,
  }: {
    name?: string;
    step?: number;
    theme?: DefaultTheme;
  }) =>
    name === "Measuerements" &&
    step === 1 &&
    theme?.isLargeScreen &&
    css`
      flex-basis: auto;
    `}
  ${({ direction }: { direction?: string }) =>
    direction === "row"
      ? css`
          flex-direction: row;
          flex-wrap: wrap;
        `
      : css`
          flex-direction: column;
        `};
  gap: ${SpaceType.Basic}px;

  ${({ showAll }: { showAll?: boolean }) =>
    !showAll &&
    css`
      height: 0px;
      overflow: hidden;
    `}
`;

const ButtonContainer = styled.View`
  padding-horizontal: ${SpaceType.Double}px;
  margin-top: ${-SpaceType.Extra}px;
`;

const TextContainer = styled.View<{ showGap?: boolean }>`
  margin-left: ${SpaceType.Double}px;
  padding-top: ${SpaceType.Extra}px;
  ${({ showGap }: { showGap?: boolean }) =>
    showGap &&
    css`
      margin-bottom: ${SpaceType.Double}px;
    `}
`;

export default memo(ListSection);
